import { useState, useEffect } from 'react';

/**
 * TierFormModal - Modal component for creating and editing subscription tiers
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Callback to close the modal
 * @param {function} props.onSubmit - Callback to handle form submission
 * @param {Object|null} props.tier - Tier object for edit mode, null for create mode
 * @param {boolean} props.isSubmitting - Whether form is currently submitting
 * @param {Object} props.specialTierIds - Object with freeTierId and waitingListTierId
 */
export default function TierFormModal({ isOpen, onClose, onSubmit, tier, isSubmitting = false, specialTierIds }) {
  const isEditMode = Boolean(tier?.id);
  const [activeTab, setActiveTab] = useState('basic');
  const [featuresSubTab, setFeaturesSubTab] = useState('general');

  // Check if current tier is a special tier (waiting list or free tier)
  const isSpecialTier = 
    specialTierIds && 
    tier && 
    (tier.id === specialTierIds.freeTierId || tier.id === specialTierIds.waitingListTierId);

  const buildInitialForm = (tierObj) => {
    const features = tierObj?.features || {};
    const exp = features.export || {};
    const col = features.collection || {};
    const srch = features.search || {};
    
    return {
      name: tierObj?.name || '',
      description: tierObj?.description || '',
      // Whether this tier should be shown on the public website
      show_on_website: tierObj?.show_on_website ?? false,
      price_monthly_cents: tierObj?.price_monthly_cents 
        ? (tierObj.price_monthly_cents / 100).toFixed(2) 
        : '',
      price_annual_cents: tierObj?.price_annual_cents 
        ? (tierObj.price_annual_cents / 100).toFixed(2) 
        : '',
      stripe_price_id_monthly: tierObj?.stripe_price_id_monthly || '',
      stripe_price_id_annual: tierObj?.stripe_price_id_annual || '',
      max_clips_per_export: exp.maxClipsPerExport?.toString() || '',
      clip_retention_days: srch.maxDays?.toString() || '',
      advanced_search_filters: srch.advancedFilters || false,
      daily_export_limit: exp.dailyLimit?.toString() || '',
      max_export_templates: exp.maxTemplates?.toString() || '',
      max_storage_mb: exp.maxStorageMb?.toString() || '',
      concurrent_exports: exp.concurrentLimit?.toString() || '',
      retention_days: exp.retentionDays?.toString() || '',
      export_enabled: exp.enabled !== false,
      export_own_channel_only: exp.ownChannelOnly || false,
      collection_enabled: col.enabled !== false,
      collection_max_clips: col.maxClips?.toString() || '',
      collection_own_channel_only: col.ownChannelOnly || false,
      collection_advanced_search_filters: col.advancedFilters || false,
      collection_tagging: col.tagging || false,
      rank: tierObj?.rank?.toString() || '',
      status: tierObj?.status || 'active',
      is_default: tierObj?.is_default || false,
      // Favorites feature fields
      favorites_channels_enabled: tierObj?.features?.favorites?.channelsEnabled ?? false,
      favorites_max_channels: tierObj?.features?.favorites?.maxChannels != null
        ? tierObj.features.favorites.maxChannels.toString()
        : '0',
      // Playback feature fields
      playback_enabled: tierObj?.features?.playback?.enabled ?? false,
      playback_ad_enabled: tierObj?.features?.playback?.ad_enabled ?? false,
      playback_ad_provider: tierObj?.features?.playback?.ad_provider || 'adsense',
      playback_ad_unit_id: tierObj?.features?.playback?.ad_unit_id || '',
      playback_ad_frequency: tierObj?.features?.playback?.ad_frequency != null
        ? tierObj.features.playback.ad_frequency.toString()
        : '5',
      playback_ad_min_interval_seconds: tierObj?.features?.playback?.ad_min_interval_seconds != null
        ? tierObj.features.playback.ad_min_interval_seconds.toString()
        : '300',
      playback_ad_preferred_duration: tierObj?.features?.playback?.ad_preferred_duration || 'short',
      playback_ad_max_duration_seconds: tierObj?.features?.playback?.ad_max_duration_seconds != null
        ? tierObj.features.playback.ad_max_duration_seconds.toString()
        : '30',
      playback_ad_skippable: tierObj?.features?.playback?.ad_skippable ?? true,
      playback_ad_skip_after_seconds: tierObj?.features?.playback?.ad_skip_after_seconds != null
        ? tierObj.features.playback.ad_skip_after_seconds.toString()
        : '5',
      playback_ad_tracking_enabled: tierObj?.features?.playback?.ad_tracking_enabled ?? true,
    };
  };

  // Form state initialized from prop on mount (relies on parent providing a `key` to remount)
  const [formData, setFormData] = useState(() => buildInitialForm(tier));

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [initialized, setInitialized] = useState(false);

  // When modal opens, mark initialized (formData already set synchronously on mount)
  useEffect(() => {
    if (!isOpen) {
      setInitialized(false);
      return;
    }
    setInitialized(true);
  }, [isOpen, tier]);

  // When tier data changes, update form data to reflect new values
  useEffect(() => {
    if (isEditMode && tier && initialized) {
      setFormData(buildInitialForm(tier));
      setErrors({});
      setTouched({});
    }
  }, [tier?.id, tier?.name, tier?.features]);

  // Validation rules
  const validate = (fieldName, value) => {
    const newErrors = { ...errors };

    switch (fieldName) {
      case 'name':
        if (!value.trim()) {
          newErrors.name = 'Name is required';
        } else if (value.length > 100) {
          newErrors.name = 'Name must be 100 characters or less';
        } else {
          delete newErrors.name;
        }
        break;

      case 'description':
        if (value && value.length > 500) {
          newErrors.description = 'Description must be 500 characters or less';
        } else {
          delete newErrors.description;
        }
        break;

      case 'price_monthly_cents':
      case 'price_annual_cents':
        if (value !== '' && (isNaN(value) || parseFloat(value) < 0)) {
          newErrors[fieldName] = 'Price must be a non-negative number';
        } else if (value !== '' && !/^\d+(\.\d{0,2})?$/.test(value)) {
          newErrors[fieldName] = 'Price must have at most 2 decimal places';
        } else {
          delete newErrors[fieldName];
        }
        break;

      case 'stripe_price_id_monthly':
      case 'stripe_price_id_annual':
        if (value && !value.startsWith('price_')) {
          newErrors[fieldName] = 'Stripe price ID must start with "price_"';
        } else {
          delete newErrors[fieldName];
        }
        break;

      case 'max_clips_per_export':
      case 'clip_retention_days':
      case 'daily_export_limit':
      case 'max_export_templates':
      case 'max_storage_mb':
      case 'concurrent_exports':
      case 'retention_days':
      case 'collection_max_clips':
        if (value !== '' && (isNaN(value) || parseInt(value) < 0)) {
          newErrors[fieldName] = 'Must be a non-negative number';
        } else if (value !== '' && !Number.isInteger(parseFloat(value))) {
          newErrors[fieldName] = 'Must be a whole number';
        } else {
          delete newErrors[fieldName];
        }
        break;

      case 'rank':
        if (value !== '' && (isNaN(value) || parseInt(value) < 0)) {
          newErrors.rank = 'Rank must be a non-negative number';
        } else {
          delete newErrors.rank;
        }
        break;
      case 'favorites_max_channels':
        // Validate only when field visible or non-empty
        /* c8 ignore start */
        if (formData.favorites_channels_enabled) {
          if (value === '' || isNaN(value) || parseInt(value) < 1) {
            newErrors.favorites_max_channels = 'Must be a whole number of at least 1';
          } else if (!Number.isInteger(parseFloat(value))) {
            newErrors.favorites_max_channels = 'Must be a whole number';
          } else if (parseInt(value) > 100) {
            newErrors.favorites_max_channels = 'Must be 100 or less';
          } else {
            delete newErrors.favorites_max_channels;
          }
        } else {
          // when disabled, allow 0 or empty
          if (value !== '' && (isNaN(value) || parseInt(value) < 0)) {
            newErrors.favorites_max_channels = 'Must be a non-negative number';
          } else if (value !== '' && !Number.isInteger(parseFloat(value))) {
            newErrors.favorites_max_channels = 'Must be a whole number';
          } else {
            delete newErrors.favorites_max_channels;
          }
        }
        /* c8 ignore stop */
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Validate on change if field has been touched
    if (touched[name]) {
      validate(name, newValue);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    validate(name, value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Mark all fields as touched
    const allTouched = Object.keys(formData).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);

    // Validate all fields
    let isValid = true;
    Object.keys(formData).forEach((key) => {
      if (!validate(key, formData[key])) {
        isValid = false;
      }
    });

    if (!isValid) {
      return;
    }

    // Prepare submission data
    const features = {
      /* c8 ignore start */
      export: {
        enabled: formData.export_enabled,
        maxStorageMb: formData.max_storage_mb !== '' ? parseInt(formData.max_storage_mb) : 100,
        retentionDays: formData.retention_days !== '' ? parseInt(formData.retention_days) : 30,
        maxClipsPerExport: formData.max_clips_per_export !== '' ? parseInt(formData.max_clips_per_export) : 5,
        dailyLimit: formData.daily_export_limit !== '' ? parseInt(formData.daily_export_limit) : null,
        concurrentLimit: formData.concurrent_exports !== '' ? parseInt(formData.concurrent_exports) : 1,
        maxTemplates: formData.max_export_templates !== '' ? parseInt(formData.max_export_templates) : 3,
        ownChannelOnly: formData.export_own_channel_only,
      },
      collection: {
        enabled: formData.collection_enabled,
        maxClips: formData.collection_max_clips !== '' ? parseInt(formData.collection_max_clips) : 15,
        ownChannelOnly: formData.collection_own_channel_only,
        advancedFilters: formData.collection_advanced_search_filters,
        tagging: formData.collection_tagging,
      },
      search: {
        maxDays: formData.clip_retention_days !== '' ? parseInt(formData.clip_retention_days) : 7,
        advancedFilters: formData.advanced_search_filters,
      },
      // Favorites feature
      favorites: {
        channelsEnabled: formData.favorites_channels_enabled,
        maxChannels: formData.favorites_max_channels !== '' ? parseInt(formData.favorites_max_channels) : 0,
      },
      // Playback feature
      playback: {
        enabled: !!formData.playback_enabled,
        ad_enabled: !!formData.playback_ad_enabled,
        ad_provider: formData.playback_ad_provider || 'adsense',
        ad_unit_id: formData.playback_ad_unit_id?.trim() || '',
        ad_frequency: formData.playback_ad_frequency !== '' ? parseInt(formData.playback_ad_frequency) : 5,
        ad_min_interval_seconds: formData.playback_ad_min_interval_seconds !== ''
          ? parseInt(formData.playback_ad_min_interval_seconds)
          : 300,
        ad_preferred_duration: formData.playback_ad_preferred_duration || 'short',
        ad_max_duration_seconds: formData.playback_ad_max_duration_seconds !== ''
          ? parseInt(formData.playback_ad_max_duration_seconds)
          : 30,
        ad_skippable: !!formData.playback_ad_skippable,
        ad_skip_after_seconds: formData.playback_ad_skip_after_seconds !== ''
          ? parseInt(formData.playback_ad_skip_after_seconds)
          : 5,
        ad_tracking_enabled: !!formData.playback_ad_tracking_enabled,
      },
      /* c8 ignore stop */
    };

    const submitData = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      price_monthly_cents: formData.price_monthly_cents 
        ? Math.round(parseFloat(formData.price_monthly_cents) * 100) 
        : 0,
      price_annual_cents: formData.price_annual_cents 
        ? Math.round(parseFloat(formData.price_annual_cents) * 100) 
        : 0,
      /* c8 ignore start */
      stripe_price_id_monthly: formData.stripe_price_id_monthly?.trim() ? formData.stripe_price_id_monthly.trim() : null,
      stripe_price_id_annual: formData.stripe_price_id_annual?.trim() ? formData.stripe_price_id_annual.trim() : null,
      /* c8 ignore stop */
      features,
      show_on_website: !!formData.show_on_website,
      rank: formData.rank ? parseInt(formData.rank) : 0,
      status: formData.status,
      is_default: formData.is_default,
    };

    onSubmit(submitData);
  };

  if (!isOpen) {
    return null;
  }

  // In edit mode, wait until we've populated formData to avoid showing empty inputs
  if (isEditMode && !initialized) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={onClose}>
      <div
        className="bg-dark-800 text-white border border-dark-700 rounded-lg shadow-2xl max-w-3xl w-full max-h-[95vh] overflow-hidden flex flex-col m-4"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-dark-700 bg-linear-to-r from-dark-700 to-dark-800">
          <div className="flex items-center justify-between">
            <h2 id="modal-title" className="text-2xl font-bold text-white">
              {isEditMode ? 'Edit Tier' : 'Create New Tier'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
              aria-label="Close modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-dark-700 bg-dark-800 px-6">
          {[
            { id: 'basic', label: 'Basic Info', icon: '📋' },
            { id: 'pricing', label: 'Pricing', icon: '💰' },
            { id: 'features', label: 'Features', icon: '⭐' },
          ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-4 font-medium text-sm border-b-2 transition-all ${
                  activeTab === tab.id
                  ? 'border-blue-400 text-blue-400 bg-dark-700'
                  : 'border-transparent text-gray-400 hover:text-white hover:bg-dark-700'
              }`}
              >
              <span className="mr-1">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-6">
            {/* Basic Info Tab */}
            {activeTab === 'basic' && (
              <div className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-white mb-2">
                    Tier Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-hidden focus:ring-2 transition-all ${
                      touched.name && errors.name
                        ? 'border-red-500 focus:ring-red-500 bg-red-900/20'
                        : 'border-dark-600 bg-dark-700 text-white focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    placeholder="e.g., Pro, Standard, Enterprise"
                    disabled={isSubmitting}
                  />
                    {touched.name && errors.name && (
                      <p className="mt-2 text-sm text-red-400 flex items-center" role="alert"><span className="mr-1">⚠️</span>{errors.name}</p>
                    )}
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-semibold text-white mb-2">
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    rows={4}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-hidden focus:ring-2 transition-all ${
                      touched.description && errors.description
                        ? 'border-red-500 focus:ring-red-500 bg-red-900/20'
                        : 'border-dark-600 bg-dark-700 text-white focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    placeholder="Brief description of this tier's benefits and target audience"
                    disabled={isSubmitting}
                  />
                    {touched.description && errors.description && (
                      <p className="mt-2 text-sm text-red-400 flex items-center" role="alert"><span className="mr-1">⚠️</span>{errors.description}</p>
                    )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                      <label htmlFor="rank" className="block text-sm font-semibold text-white mb-2">
                      Display Rank
                    </label>
                    <input
                      type="number"
                      id="rank"
                      name="rank"
                      value={formData.rank}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      min="0"
                       className={`w-full px-4 py-3 border rounded-lg focus:outline-hidden focus:ring-2 transition-all ${
                         touched.rank && errors.rank
                           ? 'border-red-500 focus:ring-red-500 bg-red-900/20 text-red-400'
                           : isSpecialTier
                           ? 'border-dark-600 bg-dark-700 cursor-not-allowed text-gray-400'
                           : 'border-dark-600 bg-dark-700 text-white focus:ring-blue-500 focus:border-blue-500'
                       }`}
                      placeholder="0"
                      disabled={isSubmitting || isSpecialTier}
                    />
                      {isSpecialTier ? (
                        <p className="mt-1 text-xs text-gray-400">🔒 Rank cannot be changed for waiting list or free tiers</p>
                      ) : (
                        <p className="mt-1 text-xs text-gray-400">Lower numbers appear first</p>
                      )}
                  </div>

                  <div>
                     <label htmlFor="status" className="block text-sm font-semibold text-white mb-2">
                      Status
                    </label>
                    <select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-dark-600 rounded-lg bg-dark-700 text-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={isSubmitting || (isEditMode && tier?.is_protected)}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                     {isEditMode && tier?.is_protected && (
                       <p className="mt-1 text-xs text-gray-400">🔒 Protected tier — status cannot be changed</p>
                     )}
                  </div>
                </div>

                {!(isEditMode && tier?.id === specialTierIds?.waitingListTierId) && (
                  <div className="pt-3 border-t border-dark-700">
                    <div className="mb-3">
                      <label className="flex items-center p-3 rounded-lg bg-dark-700/10 border border-dark-700/20 cursor-pointer hover:bg-dark-700/20 transition-colors">
                        <input
                          type="checkbox"
                          name="show_on_website"
                          checked={formData.show_on_website}
                          onChange={handleChange}
                          className="w-5 h-5 text-green-400 rounded-sm focus:ring-green-500 cursor-pointer"
                          disabled={isSubmitting}
                        />
                        <span className="ml-3 font-medium text-white">
                          Show on website
                          <span className="ml-2 text-xs text-gray-400">Display this tier on the public landing and pricing pages</span>
                        </span>
                      </label>
                    </div>
                    <label className="flex items-center p-3 rounded-lg bg-blue-900/20 border border-blue-500/30 cursor-pointer hover:bg-blue-900/30 transition-colors">
                      <input
                        type="checkbox"
                        name="is_default"
                        checked={formData.is_default}
                        onChange={handleChange}
                        className="w-5 h-5 text-blue-400 rounded-sm focus:ring-blue-500 cursor-pointer"
                        disabled={isSubmitting || (isEditMode && tier?.is_default)}
                      />
                      <span className="ml-3 font-medium text-white">
                        Set as default tier for new users
                        {isEditMode && tier?.is_default && (
                          <span className="ml-2 text-xs text-gray-400">(Currently default)</span>
                        )}
                      </span>
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* Pricing Tab */}
            {activeTab === 'pricing' && (
              <div className="space-y-5">
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-300">💡 Enter prices in dollars (e.g., 9.99 for $9.99)</p>
                </div>

                <div>
                      <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <span className="w-8 h-8 rounded-full bg-blue-900/30 flex items-center justify-center text-blue-400 mr-3 text-sm">1</span>
                      Monthly Billing
                    </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="price_monthly_cents" className="block text-sm font-medium text-gray-300 mb-2">
                        Price
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-3 text-gray-400">$</span>
                        <input
                          type="text"
                          id="price_monthly_cents"
                          name="price_monthly_cents"
                          value={formData.price_monthly_cents}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          pattern="[0-9]+(\.[0-9]{0,2})?"
                           className={`w-full pl-8 pr-4 py-3 border rounded-lg focus:outline-hidden focus:ring-2 transition-all ${
                             touched.price_monthly_cents && errors.price_monthly_cents
                               ? 'border-red-500 focus:ring-red-500 bg-red-900/20'
                               : 'border-dark-600 bg-dark-700 text-white focus:ring-blue-500 focus:border-blue-500'
                           }`}
                          placeholder="0"
                          disabled={isSubmitting}
                        />
                      </div>
                       {touched.price_monthly_cents && errors.price_monthly_cents && (
                         <p className="mt-2 text-sm text-red-400 flex items-center" role="alert"><span className="mr-1">⚠️</span>{errors.price_monthly_cents}</p>
                       )}
                    </div>
                    <div>
                      <label htmlFor="stripe_price_id_monthly" className="block text-sm font-medium text-gray-300 mb-2">
                        Stripe Price ID
                      </label>
                      <input
                        type="text"
                        id="stripe_price_id_monthly"
                        name="stripe_price_id_monthly"
                        value={formData.stripe_price_id_monthly}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-hidden focus:ring-2 transition-all ${
                          touched.stripe_price_id_monthly && errors.stripe_price_id_monthly
                            ? 'border-red-500 focus:ring-red-500 bg-red-900/20'
                            : 'border-dark-600 bg-dark-700 text-white focus:ring-blue-500 focus:border-blue-500'
                        }`}
                        placeholder="price_xxx..."
                        disabled={isSubmitting}
                      />
                       {touched.stripe_price_id_monthly && errors.stripe_price_id_monthly && (
                         <p className="mt-2 text-sm text-red-400 flex items-center" role="alert"><span className="mr-1">⚠️</span>{errors.stripe_price_id_monthly}</p>
                       )}
                    </div>
                  </div>
                </div>

                <div>
                      <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <span className="w-8 h-8 rounded-full bg-green-900/30 flex items-center justify-center text-green-400 mr-3 text-sm">2</span>
                      Annual Billing
                    </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="price_annual_cents" className="block text-sm font-medium text-gray-300 mb-2">
                        Price
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-3 text-gray-400">$</span>
                        <input
                          type="text"
                          id="price_annual_cents"
                          name="price_annual_cents"
                          value={formData.price_annual_cents}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          pattern="[0-9]+(\.[0-9]{0,2})?"
                           className={`w-full pl-8 pr-4 py-3 border rounded-lg focus:outline-hidden focus:ring-2 transition-all ${
                             touched.price_annual_cents && errors.price_annual_cents
                               ? 'border-red-500 focus:ring-red-500 bg-red-900/20'
                               : 'border-dark-600 bg-dark-700 text-white focus:ring-blue-500 focus:border-blue-500'
                           }`}
                          placeholder="0"
                          disabled={isSubmitting}
                        />
                      </div>
                       {touched.price_annual_cents && errors.price_annual_cents && (
                         <p className="mt-2 text-sm text-red-400 flex items-center" role="alert"><span className="mr-1">⚠️</span>{errors.price_annual_cents}</p>
                       )}
                    </div>
                    <div>
                      <label htmlFor="stripe_price_id_annual" className="block text-sm font-medium text-gray-300 mb-2">
                        Stripe Price ID
                      </label>
                      {/* c8 ignore start */}
                      <input
                        type="text"
                        id="stripe_price_id_annual"
                        name="stripe_price_id_annual"
                        value={formData.stripe_price_id_annual}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-hidden focus:ring-2 transition-all ${
                          touched.stripe_price_id_annual && errors.stripe_price_id_annual
                            ? 'border-red-500 focus:ring-red-500 bg-red-900/20'
                            : 'border-dark-600 bg-dark-700 text-white focus:ring-blue-500 focus:border-blue-500'
                        }`}
                        placeholder="price_xxx..."
                        disabled={isSubmitting}
                      />
                      {touched.stripe_price_id_annual && errors.stripe_price_id_annual && (
                        <p className="mt-2 text-sm text-red-400 flex items-center" role="alert"><span className="mr-1">⚠️</span>{errors.stripe_price_id_annual}</p>
                      )}
                      {/* c8 ignore stop */}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Features Tab */}
            {activeTab === 'features' && (
              <div className="space-y-6">
                {/* Sub-tab navigation */}
                <div className="flex bg-dark-700 rounded-lg p-1 gap-1">
                  {[
                    { id: 'general', label: 'General', icon: '⚙️' },
                    { id: 'export', label: 'Export', icon: '📤' },
                    { id: 'collection', label: 'Collection', icon: '📂' },
                  ].map((subTab) => (
                    <button
                      key={subTab.id}
                      type="button"
                      onClick={() => setFeaturesSubTab(subTab.id)}
                        className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                          featuresSubTab === subTab.id
                            ? 'bg-dark-600 text-blue-400 shadow-sm'
                            : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      <span className="mr-1">{subTab.icon}</span>
                      {subTab.label}
                    </button>
                  ))}
                </div>

                {/* Export Features */}
                {featuresSubTab === 'export' && (
                  <div className="border border-dark-600 rounded-lg p-5 bg-linear-to-br from-orange-900/20 to-transparent">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <span className="text-2xl mr-2">📤</span> Export Features
                  </h3>
                  <div className="mb-6">
                      <label className="flex items-center p-2 cursor-pointer hover:bg-orange-900/20 rounded-sm transition-colors">
                          <input
                            type="checkbox"
                            name="export_enabled"
                            checked={formData.export_enabled}
                            onChange={handleChange}
                            className="w-5 h-5 text-orange-400 rounded-sm focus:ring-orange-500 cursor-pointer"
                            disabled={isSubmitting}
                          />
                        <span className="ml-3 font-medium text-white">Enable Export Feature</span>
                      </label>
                  </div>

                  {formData.export_enabled && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="max_clips_per_export" className="block text-sm font-medium text-gray-300 mb-2">
                            Max Clips Per Export
                          </label>
                          <input
                            type="number"
                            id="max_clips_per_export"
                            name="max_clips_per_export"
                            value={formData.max_clips_per_export}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            min="0"
                            className={`w-full px-4 py-3 border rounded-lg focus:outline-hidden focus:ring-2 transition-all ${
                               touched.max_clips_per_export && errors.max_clips_per_export
                                ? 'border-red-500 focus:ring-red-500 bg-red-900/20'
                         : 'border-dark-600 focus:ring-blue-500 focus:border-blue-300 bg-dark-700 text-white'
                            }`}
                            placeholder="5"
                            disabled={isSubmitting}
                          />
                        </div>

                        <div>
                          <label htmlFor="daily_export_limit" className="block text-sm font-medium text-gray-300 mb-2">
                            Daily Export Limit
                          </label>
                          {/* c8 ignore start */}
                          <input
                            type="number"
                            id="daily_export_limit"
                            name="daily_export_limit"
                            value={formData.daily_export_limit}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            min="1"
                            className={`w-full px-4 py-3 border rounded-lg focus:outline-hidden focus:ring-2 transition-all ${
                               touched.daily_export_limit && errors.daily_export_limit
                                ? 'border-red-500 focus:ring-red-500 bg-red-900/20'
                                : 'border-dark-600 focus:ring-blue-500 focus:border-blue-300 bg-dark-700 text-white'
                            }`}
                            placeholder="3"
                            disabled={isSubmitting}
                          />
                          {/* c8 ignore stop */}
                        </div>

                        <div>
                          <label htmlFor="concurrent_exports" className="block text-sm font-medium text-gray-300 mb-2">
                            Concurrent Exports
                          </label>
                          {/* c8 ignore start */}
                          <input
                            type="number"
                            id="concurrent_exports"
                            name="concurrent_exports"
                            value={formData.concurrent_exports}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            min="1"
                            className={`w-full px-4 py-3 border rounded-lg focus:outline-hidden focus:ring-2 transition-all ${
                               touched.concurrent_exports && errors.concurrent_exports
                                ? 'border-red-500 focus:ring-red-500 bg-red-900/20'
                                : 'border-dark-600 focus:ring-blue-500 focus:border-blue-300 bg-dark-700 text-white'
                            }`}
                            placeholder="1"
                            disabled={isSubmitting}
                          />
                          {/* c8 ignore stop */}
                        </div>

                        <div>
                          <label htmlFor="max_export_templates" className="block text-sm font-medium text-gray-300 mb-2">
                            Max Export Templates
                          </label>
                          {/* c8 ignore start */}
                          <input
                            type="number"
                            id="max_export_templates"
                            name="max_export_templates"
                            value={formData.max_export_templates}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            min="1"
                            className={`w-full px-4 py-3 border rounded-lg focus:outline-hidden focus:ring-2 transition-all ${
                               touched.max_export_templates && errors.max_export_templates
                                ? 'border-red-500 focus:ring-red-500 bg-red-900/20'
                                : 'border-dark-600 focus:ring-blue-500 focus:border-blue-300 bg-dark-700 text-white'
                            }`}
                            placeholder="3"
                            disabled={isSubmitting}
                          />
                          {/* c8 ignore stop */}
                        </div>
                      </div>

                      {/* Own Channel Only for Export */}
                        <div className="mt-6 border-t border-orange-500/30 pt-6">
                          <label className="flex items-center p-2 cursor-pointer hover:bg-orange-900/20 rounded-sm transition-colors">
                            <input
                              type="checkbox"
                              name="export_own_channel_only"
                              checked={formData.export_own_channel_only}
                              onChange={handleChange}
                              className="w-5 h-5 text-orange-400 rounded-sm focus:ring-orange-500 cursor-pointer"
                              disabled={isSubmitting}
                            />
                            <span className="ml-3 font-medium text-white">Own Channel Only</span>
                          </label>
                        </div>

                      {/* Storage & Retention */}
                        <div className="mt-6 border-t border-orange-500/30 pt-6">
                        <div className="border border-dark-600 rounded-lg p-5 bg-linear-to-br from-amber-900/20 to-transparent">
                            <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                              <span className="text-2xl mr-2">💾</span> Storage & Retention
                            </h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label htmlFor="max_storage_mb" className="block text-sm font-medium text-gray-300 mb-2">
                                Max Storage (MB)
                              </label>
                              {/* c8 ignore start */}
                              <input
                                type="number"
                                id="max_storage_mb"
                                name="max_storage_mb"
                                value={formData.max_storage_mb}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                min="1"
                                className={`w-full px-4 py-3 border rounded-lg focus:outline-hidden focus:ring-2 transition-all ${
                                   touched.max_storage_mb && errors.max_storage_mb
                                     ? 'border-red-500 focus:ring-red-500 bg-red-900/20'
                                     : 'border-dark-600 focus:ring-blue-500 focus:border-blue-300 bg-dark-700 text-white'
                                }`}
                                placeholder="100"
                                disabled={isSubmitting}
                              />
                              {/* c8 ignore stop */}
                            </div>

                            <div>
                              <label htmlFor="retention_days" className="block text-sm font-medium text-gray-300 mb-2">
                                Data Retention (days)
                              </label>
                              {/* c8 ignore start */}
                              <input
                                type="number"
                                id="retention_days"
                                name="retention_days"
                                value={formData.retention_days}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                min="1"
                                className={`w-full px-4 py-3 border rounded-lg focus:outline-hidden focus:ring-2 transition-all ${
                                   touched.retention_days && errors.retention_days
                                     ? 'border-red-500 focus:ring-red-500 bg-red-900/20'
                                     : 'border-dark-600 focus:ring-blue-500 focus:border-blue-300 bg-dark-700 text-white'
                                }`}
                                placeholder="30"
                                disabled={isSubmitting}
                              />
                              {/* c8 ignore stop */}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  </div>
                )}

                {/* Collection Features */}
                {featuresSubTab === 'collection' && (
                  <div className="border border-dark-600 rounded-lg p-5 bg-linear-to-br from-cyan-900/20 to-transparent">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                      <span className="text-2xl mr-2">📂</span> Collection Features
                    </h3>
                    <div className="mb-6">
                      <label className="flex items-center p-2 cursor-pointer hover:bg-cyan-900/20 rounded-sm transition-colors">
                        <input
                          type="checkbox"
                          name="collection_enabled"
                          checked={formData.collection_enabled}
                          onChange={handleChange}
                          className="w-5 h-5 text-cyan-600 rounded-sm focus:ring-cyan-500 cursor-pointer"
                          disabled={isSubmitting}
                        />
                        <span className="ml-3 font-medium text-white">Enable Collections</span>
                      </label>
                    </div>

                    {formData.collection_enabled && (
                      <>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <label htmlFor="collection_max_clips" className="block text-sm font-medium text-gray-300 mb-2">
                              Max Collection Clips
                            </label>
                          {/* c8 ignore start */}
                          <input
                              type="number"
                              id="collection_max_clips"
                              name="collection_max_clips"
                              value={formData.collection_max_clips}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              min="0"
                              className={`w-full px-4 py-3 border rounded-lg focus:outline-hidden focus:ring-2 transition-all ${
                                touched.collection_max_clips && errors.collection_max_clips
                                  ? 'border-red-500 focus:ring-red-500 bg-red-900/20 text-red-400'
                                  : 'border-dark-600 bg-dark-700 text-white focus:ring-blue-500 focus:border-blue-500'
                          }`}
                              placeholder="0"
                              disabled={isSubmitting}
                            />
                          {/* c8 ignore stop */}
                          </div>
                        </div>
                        <div className="pt-4 border-t border-cyan-500/30">
                           <label className="flex items-center p-2 cursor-pointer hover:bg-cyan-900/20 rounded-sm transition-colors">
                            <input
                              type="checkbox"
                              name="collection_own_channel_only"
                              checked={formData.collection_own_channel_only}
                              onChange={handleChange}
                              className="w-5 h-5 text-cyan-400 rounded-sm focus:ring-cyan-500 cursor-pointer"
                              disabled={isSubmitting}
                            />
                            <span className="ml-3 font-medium text-white">Own Channel Only</span>
                          </label>
                        </div>
                        <div className="pt-6 border-t border-cyan-500/30">
                           <label className="flex items-center p-2 cursor-pointer hover:bg-cyan-900/20 rounded-sm transition-colors">
                            <input
                              type="checkbox"
                              name="collection_advanced_search_filters"
                              checked={formData.collection_advanced_search_filters}
                              onChange={handleChange}
                              className="w-5 h-5 text-cyan-400 rounded-sm focus:ring-cyan-500 cursor-pointer"
                              disabled={isSubmitting}
                            />
                            <span className="ml-3 font-medium text-white">Advanced Search Filters</span>
                          </label>
                        </div>
                        <div className="pt-6 border-t border-cyan-500/30">
                           <label className="flex items-center p-2 cursor-pointer hover:bg-cyan-900/20 rounded-sm transition-colors">
                            <input
                              type="checkbox"
                              name="collection_tagging"
                              checked={formData.collection_tagging}
                              onChange={handleChange}
                              className="w-5 h-5 text-cyan-400 rounded-sm focus:ring-cyan-500 cursor-pointer"
                              disabled={isSubmitting}
                            />
                            <span className="ml-3 font-medium text-white">Enable Collection Tagging</span>
                          </label>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* General Features */}
                {featuresSubTab === 'general' && (
                  <div className="space-y-6">
                    {/* Clip Features */}
                    <div className="border border-dark-600 rounded-lg p-5 bg-linear-to-br from-purple-900/20 to-transparent">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <span className="text-2xl mr-2">🔎</span> Search
                  </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                      <label htmlFor="clip_retention_days" className="block text-sm font-medium text-gray-300 mb-2">
                        Maximum Days
                      </label>
                      {/* c8 ignore start */}
                      <input
                        type="number"
                        id="clip_retention_days"
                        name="clip_retention_days"
                        value={formData.clip_retention_days}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        min="1"
                        max="90"
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-hidden focus:ring-2 transition-all ${
                          touched.clip_retention_days && errors.clip_retention_days
                            ? 'border-red-500 focus:ring-red-500 bg-red-900/20 text-red-400'
                            : 'border-dark-600 bg-dark-700 text-white focus:ring-blue-500 focus:border-blue-500'
                        }`}
                        placeholder="7"
                        disabled={isSubmitting}
                      />
                      {/* c8 ignore stop */}
                    </div>
                  </div>

                  {/* Advanced Search Filters */}
                  <div className="mt-6 border-t border-purple-500/30 pt-6">
                    <label className="flex items-center p-2 cursor-pointer hover:bg-purple-900/20 rounded-sm transition-colors">
                     <input
                       type="checkbox"
                       name="advanced_search_filters"
                       checked={formData.advanced_search_filters}
                       onChange={handleChange}
                       className="w-5 h-5 text-purple-600 rounded-sm focus:ring-purple-500 cursor-pointer"
                       disabled={isSubmitting}
                     />
                      <span className="ml-3 font-medium text-white">Advanced Search Filters</span>
                   </label>
                 </div>
                  {/* Favorites Section */}
                  <div className="mt-6 border-t border-yellow-500/30 pt-6">
                    <h4 className="text-md font-semibold text-white mb-3 flex items-center">
                      <span className="text-xl mr-2">⭐</span> Favorites
                    </h4>

                    <div className="mb-4">
                      <label className="flex items-center p-2 cursor-pointer hover:bg-yellow-900/10 rounded-sm transition-colors">
                        <input
                          type="checkbox"
                          name="favorites_channels_enabled"
                          checked={formData.favorites_channels_enabled}
                          onChange={handleChange}
                          className="w-5 h-5 text-yellow-400 rounded-sm focus:ring-yellow-500 cursor-pointer"
                          disabled={isSubmitting}
                        />
                        <span className="ml-3 font-medium text-white">Enable Favorite Channels</span>
                      </label>
                    </div>

                    {formData.favorites_channels_enabled && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="favorites_max_channels" className="block text-sm font-medium text-gray-300 mb-2">
                            Max Channels
                          </label>
                          <input
                            type="number"
                            id="favorites_max_channels"
                            name="favorites_max_channels"
                            value={formData.favorites_max_channels}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            min="1"
                            max="100"
                            className={`w-full px-4 py-3 border rounded-lg focus:outline-hidden focus:ring-2 transition-all ${
                              touched.favorites_max_channels && errors.favorites_max_channels
                                ? 'border-red-500 focus:ring-red-500 bg-red-900/20 text-red-400'
                                : 'border-dark-600 bg-dark-700 text-white focus:ring-blue-500 focus:border-blue-500'
                            }`}
                            placeholder="0"
                            disabled={isSubmitting}
                          />
                          {touched.favorites_max_channels && errors.favorites_max_channels && (
                            <p className="mt-2 text-sm text-red-400" role="alert">{errors.favorites_max_channels}</p>
                          )}
                        </div>
                      </div>
                    )}
                    {/* Playback Section */}
                    {formData.favorites_channels_enabled && (
                      <div className="mt-6 border-t border-yellow-500/30 pt-6">
                        <h4 className="text-md font-semibold text-white mb-3 flex items-center">
                          <span className="text-xl mr-2">▶️</span> Playback
                        </h4>

                        <div className="mb-4">
                          <label className="flex items-center p-2 cursor-pointer hover:bg-yellow-900/10 rounded-sm transition-colors">
                            <input
                              type="checkbox"
                              name="playback_enabled"
                              checked={formData.playback_enabled}
                              onChange={handleChange}
                              className="w-5 h-5 text-yellow-400 rounded-sm focus:ring-yellow-500 cursor-pointer"
                              disabled={isSubmitting}
                            />
                            <span className="ml-3 font-medium text-white">Enable Playback</span>
                          </label>
                        </div>

                        {formData.playback_enabled && (
                          <div className="mt-4 border border-yellow-400/10 rounded-lg p-4 bg-dark-700">
                            <h5 className="text-sm text-gray-300 mb-2">Ad Configuration</h5>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label htmlFor="playback_ad_enabled" className="flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    id="playback_ad_enabled"
                                    name="playback_ad_enabled"
                                    checked={formData.playback_ad_enabled}
                                    onChange={handleChange}
                                    className="w-5 h-5 text-yellow-400 rounded-sm focus:ring-yellow-500 cursor-pointer"
                                    disabled={isSubmitting}
                                  />
                                  <span className="ml-3 text-white">Enable Ads</span>
                                </label>
                              </div>

                              <div>
                                <label htmlFor="playback_ad_unit_id" className="block text-sm font-medium text-gray-300 mb-2">
                                  Ad Unit ID
                                </label>
                                <input
                                  type="text"
                                  id="playback_ad_unit_id"
                                  name="playback_ad_unit_id"
                                  value={formData.playback_ad_unit_id}
                                  onChange={handleChange}
                                  className="w-full px-3 py-2 rounded bg-dark-800 border border-dark-600 text-white"
                                  disabled={!formData.playback_ad_enabled || isSubmitting}
                                />
                              </div>

                              {formData.playback_ad_enabled && (
                                <>
                                  <div>
                                    <label htmlFor="playback_ad_frequency" className="block text-sm font-medium text-gray-300 mb-2">Show ad every N clips</label>
                                    <input
                                      type="number"
                                      id="playback_ad_frequency"
                                      name="playback_ad_frequency"
                                      value={formData.playback_ad_frequency}
                                      onChange={handleChange}
                                      min="1"
                                      className="w-full px-3 py-2 rounded bg-dark-800 border border-dark-600 text-white"
                                      disabled={isSubmitting}
                                    />
                                  </div>

                                  <div>
                                    <label htmlFor="playback_ad_min_interval_seconds" className="block text-sm font-medium text-gray-300 mb-2">Min seconds between ads</label>
                                    <input
                                      type="number"
                                      id="playback_ad_min_interval_seconds"
                                      name="playback_ad_min_interval_seconds"
                                      value={formData.playback_ad_min_interval_seconds}
                                      onChange={handleChange}
                                      min="0"
                                      className="w-full px-3 py-2 rounded bg-dark-800 border border-dark-600 text-white"
                                      disabled={isSubmitting}
                                    />
                                  </div>

                                  <div>
                                    <label htmlFor="playback_ad_preferred_duration" className="block text-sm font-medium text-gray-300 mb-2">Preferred duration</label>
                                    <select
                                      id="playback_ad_preferred_duration"
                                      name="playback_ad_preferred_duration"
                                      value={formData.playback_ad_preferred_duration}
                                      onChange={handleChange}
                                      className="w-full px-3 py-2 rounded bg-dark-800 border border-dark-600 text-white"
                                      disabled={isSubmitting}
                                    >
                                      <option value="short">Short</option>
                                      <option value="medium">Medium</option>
                                      <option value="long">Long</option>
                                    </select>
                                  </div>

                                  <div>
                                    <label htmlFor="playback_ad_max_duration_seconds" className="block text-sm font-medium text-gray-300 mb-2">Max ad duration (seconds)</label>
                                    <input
                                      type="number"
                                      id="playback_ad_max_duration_seconds"
                                      name="playback_ad_max_duration_seconds"
                                      value={formData.playback_ad_max_duration_seconds}
                                      onChange={handleChange}
                                      min="5"
                                      className="w-full px-3 py-2 rounded bg-dark-800 border border-dark-600 text-white"
                                      disabled={isSubmitting}
                                    />
                                  </div>

                                  <div>
                                    <label className="flex items-center cursor-pointer">
                                      <input
                                        type="checkbox"
                                        id="playback_ad_skippable"
                                        name="playback_ad_skippable"
                                        checked={formData.playback_ad_skippable}
                                        onChange={handleChange}
                                        className="w-5 h-5 text-yellow-400 rounded-sm focus:ring-yellow-500 cursor-pointer"
                                        disabled={isSubmitting}
                                      />
                                      <span className="ml-3 text-white">Allow skip</span>
                                    </label>
                                  </div>

                                  {formData.playback_ad_skippable && (
                                    <div>
                                      <label htmlFor="playback_ad_skip_after_seconds" className="block text-sm font-medium text-gray-300 mb-2">Skip after (seconds)</label>
                                      <input
                                        type="number"
                                        id="playback_ad_skip_after_seconds"
                                        name="playback_ad_skip_after_seconds"
                                        value={formData.playback_ad_skip_after_seconds}
                                        onChange={handleChange}
                                        min="0"
                                        className="w-full px-3 py-2 rounded bg-dark-800 border border-dark-600 text-white"
                                        disabled={isSubmitting}
                                      />
                                    </div>
                                  )}

                                  <div>
                                    <label className="flex items-center cursor-pointer">
                                      <input
                                        type="checkbox"
                                        id="playback_ad_tracking_enabled"
                                        name="playback_ad_tracking_enabled"
                                        checked={formData.playback_ad_tracking_enabled}
                                        onChange={handleChange}
                                        className="w-5 h-5 text-yellow-400 rounded-sm focus:ring-yellow-500 cursor-pointer"
                                        disabled={isSubmitting}
                                      />
                                      <span className="ml-3 text-white">Enable ad tracking</span>
                                    </label>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-dark-700 bg-dark-700 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 text-gray-300 bg-dark-800 border border-dark-600 rounded-lg hover:bg-dark-600 focus:outline-hidden focus:ring-2 focus:ring-gray-500 font-medium transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="px-6 py-2.5 text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 disabled:bg-dark-600 disabled:cursor-not-allowed font-medium transition-colors"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="flex items-center">
                <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </span>
            ) : isEditMode ? (
              'Update Tier'
            ) : (
              'Create Tier'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
